/**
 * User display preferences. Stored locally per device under
 * `cleanplate.prefs.v1`. Loaded synchronously by an inline script in
 * `index.html` before React mounts so theme/font/size apply without a flash.
 *
 * Keep the shape stable; bump the storage key (e.g. `.v2`) on breaking changes.
 */

export type RecipeFont = "serif" | "sans";
export type TextSize = "sm" | "md" | "lg";
export type Contrast = "light" | "dark" | "system";
export type UnitSystem = "metric" | "imperial" | "auto";

/**
 * Diet preference. Either one of the built-in presets, a free-text custom
 * label (e.g. "Pescatarian", "Dairy-free"), or `none`.
 *
 * Stored as a discriminated union so we can tell preset vs custom apart and
 * keep the displayed label authoritative — the AI tagger receives the label
 * verbatim, and the recipe card highlights the tag whose normalised text
 * matches the label.
 */
export type DietPreference =
  | { kind: "none" }
  | { kind: "preset"; value: "vegan" | "vegetarian" | "gluten-free" }
  | { kind: "custom"; label: string };

export type Preferences = {
  font: RecipeFont;
  textSize: TextSize;
  contrast: Contrast;
  /** Keep screen awake while a recipe page is mounted. */
  cookingMode: boolean;
  /** Tap a method step to mark it done (in-memory, per recipe). */
  stepHighlight: boolean;
  /** Tap an ingredient to tick it off (in-memory, per recipe). */
  ingredientCheckoff: boolean;
  /** Metric / Imperial / Auto (locale-derived). Available to all tiers. */
  units: UnitSystem;
  /** Diet preference. Available to all tiers. */
  diet: DietPreference;
};

export const PREFS_STORAGE_KEY = "cleanplate.prefs.v3";
const LEGACY_PREFS_KEYS = ["cleanplate.prefs.v2", "cleanplate.prefs.v1"];

export const defaultPrefs: Preferences = {
  font: "sans",
  textSize: "md",
  contrast: "light",
  cookingMode: false,
  stepHighlight: true,
  ingredientCheckoff: true,
  units: "auto",
  diet: { kind: "none" },
};

const isFont = (v: unknown): v is RecipeFont => v === "serif" || v === "sans";
const isSize = (v: unknown): v is TextSize =>
  v === "sm" || v === "md" || v === "lg";
const isContrast = (v: unknown): v is Contrast =>
  v === "light" || v === "dark" || v === "system";
const isUnits = (v: unknown): v is UnitSystem =>
  v === "metric" || v === "imperial" || v === "auto";

const PRESET_DIETS = ["vegan", "vegetarian", "gluten-free"] as const;
const isDiet = (v: unknown): v is DietPreference => {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  if (o.kind === "none") return true;
  if (o.kind === "preset" && typeof o.value === "string" && (PRESET_DIETS as readonly string[]).includes(o.value)) {
    return true;
  }
  if (o.kind === "custom" && typeof o.label === "string" && o.label.trim().length > 0) return true;
  return false;
};

/**
 * Human-readable label for a diet preference (used in UI & sent to the
 * recipe-cleaning AI). Returns null when no preference is set.
 */
export const dietLabel = (d: DietPreference): string | null => {
  if (d.kind === "none") return null;
  if (d.kind === "preset") {
    if (d.value === "vegan") return "Vegan";
    if (d.value === "vegetarian") return "Vegetarian";
    return "Gluten-free";
  }
  return d.label.trim().slice(0, 40);
};

export function loadPrefs(): Preferences {
  if (typeof window === "undefined") return defaultPrefs;
  try {
    let raw = window.localStorage.getItem(PREFS_STORAGE_KEY);
    // One-time migration from older keys (v1 → v2 added `units`; v2 → v3
    // added `diet`). Legacy entries are copied into the current key & removed.
    if (!raw) {
      for (const legacyKey of LEGACY_PREFS_KEYS) {
        const legacy = window.localStorage.getItem(legacyKey);
        if (legacy) {
          raw = legacy;
          try {
            window.localStorage.setItem(PREFS_STORAGE_KEY, legacy);
            window.localStorage.removeItem(legacyKey);
          } catch { /* ignore */ }
          break;
        }
      }
    }
    if (!raw) return defaultPrefs;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      font: isFont(parsed.font) ? parsed.font : defaultPrefs.font,
      textSize: isSize(parsed.textSize) ? parsed.textSize : defaultPrefs.textSize,
      contrast: isContrast(parsed.contrast) ? parsed.contrast : defaultPrefs.contrast,
      cookingMode: !!parsed.cookingMode,
      stepHighlight: !!parsed.stepHighlight,
      ingredientCheckoff: !!parsed.ingredientCheckoff,
      units: isUnits(parsed.units) ? parsed.units : defaultPrefs.units,
      diet: isDiet(parsed.diet) ? parsed.diet : defaultPrefs.diet,
    };
  } catch {
    return defaultPrefs;
  }
}

export function savePrefs(prefs: Preferences): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore quota errors */
  }
}

const SERIF_STACK = "'Fraunces', Georgia, serif";
const SANS_STACK = "'Inter', system-ui, sans-serif";

/**
 * Apply preferences to <html>: dark class, data-text-size, font CSS vars.
 * Safe to call from both the inline boot script and React.
 */
export function applyPrefsToDocument(prefs: Preferences): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  // Contrast
  const isDark =
    prefs.contrast === "dark" ||
    (prefs.contrast === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  root.classList.toggle("dark", isDark);

  // Text size
  root.setAttribute("data-text-size", prefs.textSize);

  // Font: body uses chosen family, title swaps to the opposite.
  const body = prefs.font === "serif" ? SERIF_STACK : SANS_STACK;
  const title = prefs.font === "serif" ? SANS_STACK : SERIF_STACK;
  root.style.setProperty("--font-recipe-body", body);
  root.style.setProperty("--font-recipe-title", title);
}
